import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Link2, Plus, Trash2, ArrowUp, ArrowDown, Star, Eye, EyeOff, QrCode, ExternalLink, Info,
} from 'lucide-react';
import { api } from '../../lib/api';
import type { BioLink, BioLinkType, BioProps, LinksResponse } from '../../lib/types';
import { MediaPicker } from '../../components/ui/MediaPicker';
import QrCodeModal from '../../components/ui/QrCodeModal';

type Locale = 'es' | 'en';

const TYPE_OPTIONS: { value: BioLinkType; label: string }[] = [
  { value: 'link', label: 'Enlace' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'menu', label: 'Menú' },
  { value: 'maps', label: 'Cómo llegar' },
  { value: 'phone', label: 'Teléfono' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'evento', label: 'Eventos / reservas' },
];

const URL_HINT: Record<BioLinkType, string> = {
  link: 'https://...',
  whatsapp: 'Número, ej. +52 664 000 0000',
  menu: 'https://... o /menu',
  maps: 'https://maps.google.com/...',
  phone: 'Número, ej. +52 664 000 0000',
  instagram: 'https://instagram.com/...',
  evento: 'https://... o /reservas',
};

export default function LinksTabContent({ onSaved }: { onSaved?: () => void } = {}) {
  const [data, setData] = useState<LinksResponse | null>(null);
  const [locale, setLocale] = useState<Locale>('es');
  const [draft, setDraft] = useState<Record<Locale, BioProps> | null>(null);
  const [saving, setSaving] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    const res = await api.links.get();
    setData(res);
    setDraft({ es: res.locales.es.props, en: res.locales.en.props });
  }

  function patch(p: Partial<BioProps>) {
    setDraft((prev) => prev && ({ ...prev, [locale]: { ...prev[locale], ...p } }));
  }
  function patchLink(idx: number, p: Partial<BioLink>) {
    setDraft((prev) => {
      if (!prev) return prev;
      const links = prev[locale].links.map((l, i) => (i === idx ? { ...l, ...p } : l));
      return { ...prev, [locale]: { ...prev[locale], links } };
    });
  }
  function moveLink(from: number, dir: -1 | 1) {
    setDraft((prev) => {
      if (!prev) return prev;
      const links = [...prev[locale].links];
      const to = from + dir;
      if (to < 0 || to >= links.length) return prev;
      [links[from], links[to]] = [links[to], links[from]];
      return { ...prev, [locale]: { ...prev[locale], links } };
    });
  }
  function addLink() {
    setDraft((prev) => prev && ({
      ...prev,
      [locale]: { ...prev[locale], links: [...prev[locale].links, { label: '', url: '', type: 'link', featured: false, hidden: false }] },
    }));
  }
  function removeLink(idx: number) {
    setDraft((prev) => prev && ({
      ...prev,
      [locale]: { ...prev[locale], links: prev[locale].links.filter((_, i) => i !== idx) },
    }));
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.links.save({ locale, props: draft[locale], publish: true });
      await load();
      onSaved?.();
    } finally {
      setSaving(false);
    }
  }

  if (!data || !draft) {
    return <p className="text-sm text-ui-text-subtle py-8 text-center">Cargando…</p>;
  }

  const current = draft[locale];
  const state = data.locales[locale];

  return (
    <div className="space-y-5">
      <div className="flex gap-3 rounded-lg border border-ui-border bg-ui-surface px-3.5 py-3.5">
        <Link2 size={15} className="text-ui-accent shrink-0 mt-0.5" />
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium text-ui-text">Tu página de enlaces</p>
          <p className="text-sm text-ui-text-subtle leading-relaxed">
            Una sola página con todos tus botones para poner como enlace en tu biografía de Instagram:
            WhatsApp, menú, cómo llegar, eventos y más. Genera su código QR para imprimirlo en tu local.
          </p>
        </div>
      </div>

      {/* Cabecera: idioma + URL pública + QR */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 p-1 bg-ui-bg rounded-lg border border-ui-border">
          {(['es', 'en'] as Locale[]).map((loc) => (
            <button
              key={loc}
              onClick={() => setLocale(loc)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                locale === loc
                  ? 'bg-ui-surface-2 text-ui-text border border-ui-border-strong'
                  : 'text-ui-text-subtle hover:text-ui-text-muted'
              }`}
            >
              {loc.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {state.publicUrl && (
            <a
              href={state.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-ui-text-subtle hover:text-ui-accent"
            >
              <ExternalLink size={14} /> Ver página
            </a>
          )}
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            disabled={!state.publicUrl}
            title={state.publicUrl ? 'Generar código QR' : 'Configura tu dominio para generar el QR'}
            className="inline-flex items-center gap-1.5 rounded-md border border-ui-border px-3 py-1.5 text-sm text-ui-text hover:bg-ui-text/[0.05] disabled:opacity-40"
          >
            <QrCode size={15} /> Generar QR
          </button>
        </div>
      </div>

      {!data.hasDomain && (
        <div className="flex gap-2 rounded-lg border border-ui-border bg-ui-surface px-3.5 py-3 items-center">
          <Info size={14} className="text-ui-text-subtle shrink-0" />
          <p className="text-sm text-ui-text-subtle">
            Para crear el código QR y compartir tu página necesitas conectar tu dominio en la pestaña <strong className="text-ui-text-muted">Dominio</strong>.
          </p>
        </div>
      )}

      {/* Perfil */}
      <div className="rounded-lg border border-ui-border bg-ui-surface p-4 space-y-3">
        <div>
          <label className="block text-sm text-ui-text-muted mb-1.5">Foto o logo</label>
          <MediaPicker
            value={current.avatar ? [current.avatar] : []}
            onChange={(urls) => patch({ avatar: urls[0] ?? '' })}
            multiple={false}
            folder="pages"
          />
        </div>
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">Nombre</label>
          <input
            value={current.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder={data.tenantName}
            className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
          />
        </div>
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">Descripción corta</label>
          <input
            value={current.tagline}
            onChange={(e) => patch({ tagline: e.target.value })}
            placeholder="Ej. Café de especialidad en el centro · Abierto todos los días"
            className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ui-text-muted cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={current.showSocial}
            onChange={(e) => patch({ showSocial: e.target.checked })}
            className="accent-ui-accent"
          />
          Mostrar mis redes sociales (de la pestaña «Contacto y redes»)
        </label>
        <label className="flex items-start gap-2 text-sm text-ui-text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={current.hideChrome}
            onChange={(e) => patch({ hideChrome: e.target.checked })}
            className="accent-ui-accent mt-0.5"
          />
          <span>
            Versión minimalista
            <span className="block text-ui-text-subtle">Oculta el menú y el pie de página para que solo se vean tus botones.</span>
          </span>
        </label>
      </div>

      {/* Botones */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-ui-text">Botones</h3>
          <button
            type="button"
            onClick={addLink}
            className="inline-flex items-center gap-1.5 rounded-md bg-ui-accent px-3 py-1.5 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover"
          >
            <Plus size={15} /> Añadir botón
          </button>
        </div>

        {current.links.length === 0 ? (
          <p className="text-sm text-ui-text-subtle py-6 text-center border border-dashed border-ui-border rounded-lg">
            Añade tu primer botón: WhatsApp, menú, cómo llegar…
          </p>
        ) : (
          current.links.map((link, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`rounded-lg border bg-ui-surface p-3 space-y-2.5 ${link.hidden ? 'border-ui-border opacity-60' : 'border-ui-border'}`}
            >
              <div className="flex gap-2">
                <select
                  value={link.type ?? 'link'}
                  onChange={(e) => patchLink(idx, { type: e.target.value as BioLinkType })}
                  className="bg-ui-bg border border-ui-border rounded px-2 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
                >
                  {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input
                  value={link.label}
                  onChange={(e) => patchLink(idx, { label: e.target.value })}
                  placeholder="Texto del botón"
                  className="flex-1 min-w-0 bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
                />
              </div>
              <input
                value={link.url}
                onChange={(e) => patchLink(idx, { url: e.target.value })}
                placeholder={URL_HINT[link.type ?? 'link']}
                className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => patchLink(idx, { featured: !link.featured })} title="Destacar"
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm transition-colors ${link.featured ? 'text-ui-accent bg-ui-accent/10' : 'text-ui-text-subtle hover:text-ui-text'}`}>
                    <Star size={14} className={link.featured ? 'fill-current' : ''} /> Destacar
                  </button>
                  <button type="button" onClick={() => patchLink(idx, { hidden: !link.hidden })} title={link.hidden ? 'Mostrar' : 'Ocultar'}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-ui-text-subtle hover:text-ui-text transition-colors">
                    {link.hidden ? <EyeOff size={14} /> : <Eye size={14} />} {link.hidden ? 'Oculto' : 'Visible'}
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => moveLink(idx, -1)} disabled={idx === 0} title="Subir"
                    className="rounded-md border border-ui-border p-1.5 text-ui-text-muted hover:text-ui-text disabled:opacity-30"><ArrowUp size={14} /></button>
                  <button type="button" onClick={() => moveLink(idx, 1)} disabled={idx === current.links.length - 1} title="Bajar"
                    className="rounded-md border border-ui-border p-1.5 text-ui-text-muted hover:text-ui-text disabled:opacity-30"><ArrowDown size={14} /></button>
                  <button type="button" onClick={() => removeLink(idx)} title="Eliminar"
                    className="rounded-md border border-ui-border p-1.5 text-red-500 hover:bg-red-500/10"><Trash2 size={14} /></button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <p className="text-sm text-ui-text-subtle">
          Después de guardar, usa <strong className="text-ui-text-muted">«Publicar sitio»</strong> para que tus visitantes lo vean.
        </p>
      </div>

      {qrOpen && state.publicUrl && (
        <QrCodeModal
          value={state.publicUrl}
          label={`Página de enlaces${locale === 'en' ? ' (EN)' : ''}`}
          module="linkinbio"
          fileName="enlaces"
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>
  );
}
